import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, WritableSignal, signal } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Observable, of, switchMap } from 'rxjs';
import { IncidentsService, ViewIncident } from 'src/app/api/incidents.service';
import { WorkflowService, WorkflowStep } from 'src/app/api/workflow.service';
import { SplitButton } from 'primeng/splitbutton';
import { IncidentModalService } from '../../modals/incident-modal.service';
import { IncidentSeverityImpactService } from 'src/app/api/incident-severity-impact.service';
import { DialogService } from 'primeng/dynamicdialog';
import { IncidentSeverityImpactFormData } from 'src/app/api/incident-severity-impact.service';



@Component({
    selector: 'app-transition-button',
    templateUrl: './transition-button.component.html',
    styleUrls: ['./transition-button.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TransitionButtonComponent implements OnInit {
  menuItems: WritableSignal<MenuItem[]> = signal<MenuItem[]>([]);
  private _incident: ViewIncident
  @Input() set incident(value: ViewIncident) {
    this._incident = value

    if (this._incident.workItemId !== 0) {
      this.steps$ = this.workflowService.getStepsForWorkflow(value.workflowId)
      this.currentStepName$ = this.getCurrentStepName();
      if (this.initialized) {
        this.getStepMenu();
      }
    }
  }

  get incident(): ViewIncident {
    return this._incident
  }

  @Input() size: SplitButton["size"] = undefined;

  @Output() transitionComplete = new EventEmitter<ViewIncident>();

  steps$: Observable<WorkflowStep[]> = of([])
  currentStepName$!: Observable<string | undefined>;

  initialized = false;

  constructor(
    private incidentService: IncidentsService,
    private workflowService: WorkflowService,
    private incidentModalService: IncidentModalService,
    private severityImpactService: IncidentSeverityImpactService,
    private dialogService: DialogService
  ) {
    this._incident = this.incidentService.emptyViewIncident;
  }

  ngOnInit(): void {
    this.getStepMenu()
    this.initialized = true;
  }

  getStepMenu() {
    if (this.incident.workItemId > 0) {
      this.steps$.subscribe(steps => {
        steps.forEach(step => {
          this.menuItems.update(value => [...value, {
            label: step.name,
            command: () => { this.onStepClick(step) }
          }])
        })
      })
    }
  }

  getCurrentStepName() {
    return this.workflowService.getStepName(this.incident.currentStepId)
  }



  onStepClick(step: WorkflowStep) {
    // Check if this is a transition to a closed state
    this.workflowService.getWorkflowData().subscribe(workflowData => {
      const workflow = workflowData.workflows.find((w: any) => w.workflowId === this.incident.workflowId);
      
      if (workflow && step.workflowStepId === workflow.closedStepId) {
        // This is a transition to closed state, show severity impact form
        this.showSeverityImpactForm(step);
      } else {
        // Regular transition
        this.performTransition(step);
      }
    });
  }

  private showSeverityImpactForm(step: WorkflowStep) {
    // Check if severity impact data already exists for this incident
    const existingSeverityImpact = this.incident.severityImpact;
    
    this.incidentModalService.openSeverityImpactForm(this.dialogService, this.incident.workItemId, existingSeverityImpact).subscribe({
      next: (formData: IncidentSeverityImpactFormData | null) => {
        if (formData) {
          // Save severity impact data first, then transition
          const saveOperation = existingSeverityImpact 
            ? this.severityImpactService.updateSeverityImpact(this.incident.workItemId, formData)
            : this.severityImpactService.createSeverityImpact(this.incident.workItemId, formData);
          
          saveOperation.subscribe({
            next: () => {
              this.performTransition(step);
            },
            error: (error: any) => {
              console.error('Error saving severity impact data:', error);
              // Still proceed with transition even if severity impact save fails
              this.performTransition(step);
            }
          });
        } else {
          // User cancelled, don't transition
          console.log('Severity impact assessment cancelled');
        }
      },
      error: (error: any) => {
        console.error('Error opening severity impact form:', error);
        // Fallback to regular transition
        this.performTransition(step);
      }
    });
  }

  private performTransition(step: WorkflowStep) {
    this.incidentService.transitionIncident(this.incident.workItemId, step.workflowStepId).subscribe(() => {
      this.incidentService.getIncident(this.incident.workItemId).subscribe(incident => {
        this.incident = incident
        this.transitionComplete.emit(incident);
      })
    })
  }
}


